import { multiply, type Matrix } from '@flobi/artwork-core';

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BoardSize {
  width: number;
  height: number;
}

/** How far in from an edge a layer still counts as sitting against it: the outer fifth. */
const NEAR_EDGE = 0.2;

/**
 * Where a layer copied off one page lands on another, as its transform inside that page.
 *
 * `local` is the layer's transform inside the page it was copied from and `box` its bounds
 * there. On a page of the same size nothing changes: it lands exactly where it was (Farah,
 * 23 Oct). On a page of another size it keeps its size — never scaled ("no never change
 * the size", Farah, 23 Oct) — and only its place follows the layout, each side on its own:
 *   - a layer against an edge stays against it, the same distance in;
 *   - a centred one, or one running from edge to edge, stays centred;
 *   - one out in the middle keeps its share of the way across;
 *   - a layer that was inside the old page is kept inside the new one where it fits.
 */
export function placeOnBoard(local: Matrix, box: Box, from: BoardSize, to: BoardSize): Matrix {
  if (Math.abs(from.width - to.width) < 0.5 && Math.abs(from.height - to.height) < 0.5) return local;
  if (from.width <= 0 || from.height <= 0 || to.width <= 0 || to.height <= 0) return local;
  const x = side(box.x, box.width, from.width, to.width);
  const y = side(box.y, box.height, from.height, to.height);
  const move: Matrix = [1, 0, 0, 1, x - (box.x + box.width / 2), y - (box.y + box.height / 2)];
  return multiply(move, local);
}

/** Where a layer's middle goes along one side of the new page. */
const side = (at: number, size: number, from: number, to: number): number => {
  const middle = at + size / 2;
  const off = middle - from / 2;
  const slack = Math.max(1, from * 0.01);
  const spanning = at <= slack && at + size >= from - slack;
  if (spanning || Math.abs(off) <= slack) return to / 2 + off;
  const before = at;
  const after = from - (at + size);
  const half = size / 2;
  let placed: number;
  if (before <= after && before <= from * NEAR_EDGE) placed = before + half;
  else if (after < before && after <= from * NEAR_EDGE) placed = to - after - half;
  else placed = (middle / from) * to;
  // Inside the old page, inside the new one.
  if (before >= 0 && after >= 0 && size <= to) placed = Math.min(Math.max(placed, half), to - half);
  return placed;
};
